/**
 * The `/`-command palette rendered over the notes editor — §2.8 #65.
 *
 * Extracted from `NotesViewer.tsx`. Purely presentational: every piece of state
 * it reads comes from `useSlashMenu`, which is why its props are that hook's
 * return type rather than a hand-written interface. Spreading the hook
 * (`<SlashMenu {...slashMenu} />`) is the intended call shape and the reason
 * the two were split this way — the alternative, threading eight props, is the
 * same coupling with more places to get it wrong.
 *
 * Positioned `fixed` off `slashMenuPosition`, which `useSlashMenu` derives from
 * Quill's `getBounds` — i.e. it tracks the caret, not this element's ancestors,
 * so it must not be `absolute` inside the editor's scroll container.
 */
import type { SlashMenuState } from '@/hooks/lesson/useSlashMenu';

export function SlashMenu({
  showSlashMenu,
  slashMenuPosition,
  selectedCommandIndex,
  filteredCommands,
  slashMenuRef,
  isClickingMenuRef,
  applySlashCommand,
}: SlashMenuState) {
  // An open menu whose filter matches nothing renders nothing rather than an
  // empty box: the user is mid-word and about to either match or move on, and
  // a "No results" card under the caret is noise in the middle of typing.
  if (!showSlashMenu || filteredCommands.length === 0) return null;

  return (
    <div
      ref={slashMenuRef}
      className='slash-menu fixed z-50 w-64 overflow-hidden rounded-lg border border-border bg-card shadow-2xl'
      style={{
        top: `${slashMenuPosition.top}px`,
        left: `${slashMenuPosition.left}px`,
      }}
      // A labelled `group` of buttons, NOT `role='menu'` + `menuitem`.
      //
      // Real menu semantics would be a lie here: `menu` requires roving focus
      // and owned `menuitem` children, and this palette deliberately leaves
      // DOM focus in the editor (that is what lets typing keep narrowing the
      // list) and wraps its buttons in a layout div. Claiming the role without
      // the behaviour is the exact shape of the invalid ARIA already fixed on
      // `TagFilter` — `role='listbox'` over plain buttons — and axe flags the
      // wrapper as a disallowed child. `group` + `aria-current` is honest
      // about what this is: a named set of buttons, one of which is marked.
      role='group'
      aria-label='Formatting commands'
    >
      <div className='py-2'>
        {filteredCommands.map((command, index) => {
          const Icon = command.icon;
          const isSelected = index === selectedCommandIndex;
          return (
            <button
              key={command.id}
              type='button'
              // The keyboard selection was conveyed by background colour alone.
              // Arrow keys move this highlight without moving DOM focus (focus
              // stays in the editor so typing keeps filtering), so there is no
              // `:focus` for a screen reader to follow and nothing else marks
              // which command Enter would apply.
              aria-current={isSelected}
              onMouseDown={e => {
                // The menu must not steal the caret: a mousedown that blurs the
                // editor destroys the Quill selection `applySlashCommand` needs
                // to know where to delete from.
                e.preventDefault();
                e.stopPropagation();
                isClickingMenuRef.current = true;
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                applySlashCommand(command);
                setTimeout(() => {
                  isClickingMenuRef.current = false;
                }, 200);
              }}
              className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors max-sm:min-h-11 ${
                isSelected
                  ? 'dark:tint-brand bg-blue-50 text-blue-700'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon className='h-4 w-4 flex-shrink-0' aria-hidden='true' />
              <span className='text-sm font-medium'>{command.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
